/**
 * Svix Adapter
 * Wraps the Svix SDK for webhook management
 *
 * Benefits:
 * - Centralized configuration
 * - Easy to swap webhook providers
 * - Consistent error handling
 * - Feature availability checks
 */
import { Svix, WebhookVerificationError } from "svix";

import { features, serverEnv } from "@/lib/config/env";

// Singleton instance
let _svixClient: Svix | null = null;

/**
 * Check if Svix is available
 */
export function isSvixAvailable(): boolean {
  return features.hasSvix;
}

/**
 * Get Svix configuration
 */
export function getSvixConfig() {
  return {
    apiKey: serverEnv.SVIX_API_KEY,
    applicationId: serverEnv.SVIX_APP_ID,
  };
}

/**
 * Get the Svix client instance
 */
export function getSvixClient(): Svix | null {
  if (!isSvixAvailable()) {
    return null;
  }

  if (_svixClient === null) {
    const config = getSvixConfig();
    if (config.apiKey) {
      _svixClient = new Svix(config.apiKey);
    }
  }

  return _svixClient;
}

/**
 * Send a webhook message
 */
export async function sendWebhook(
  eventType: string,
  payload: Record<string, unknown>,
  options?: {
    applicationId?: string;
    idempotencyKey?: string;
  }
): Promise<{ id: string } | null> {
  const client = getSvixClient();
  if (!client) {
    console.warn("[Svix Adapter] Svix not configured, webhook not sent");
    return null;
  }

  const config = getSvixConfig();
  const appId = options?.applicationId || config.applicationId;

  if (!appId) {
    console.error("[Svix Adapter] Application ID not configured");
    return null;
  }

  try {
    const result = await client.message.create(appId, {
      eventType,
      payload,
      ...(options?.idempotencyKey && { idempotencyKey: options.idempotencyKey }),
    });
    return { id: result.id };
  } catch (error) {
    console.error("[Svix Adapter] Failed to send webhook:", error);
    throw error;
  }
}

/**
 * Verify webhook signature
 */
export function verifyWebhookSignature(
  payload: string,
  headers: {
    "svix-id"?: string;
    "svix-timestamp"?: string;
    "svix-signature"?: string;
  },
  secret: string
): boolean {
  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const wh = new (require("svix").Webhook)(secret);
    wh.verify(payload, headers);
    return true;
  } catch (error) {
    if (error instanceof WebhookVerificationError) {
      console.warn("[Svix Adapter] Webhook verification failed:", error.message);
      return false;
    }
    throw error;
  }
}

/**
 * Create a webhook endpoint
 */
export async function createEndpoint(
  url: string,
  options?: {
    applicationId?: string;
    description?: string;
    filterTypes?: string[];
  }
): Promise<{ id: string; url: string } | null> {
  const client = getSvixClient();
  if (!client) {
    return null;
  }

  const config = getSvixConfig();
  const appId = options?.applicationId || config.applicationId;

  if (!appId) {
    return null;
  }

  try {
    const result = await client.endpoint.create(appId, {
      url,
      description: options?.description,
      filterTypes: options?.filterTypes,
    });
    return { id: result.id, url: result.url };
  } catch (error) {
    console.error("[Svix Adapter] Failed to create endpoint:", error);
    throw error;
  }
}

/**
 * List webhook endpoints
 */
export async function listEndpoints(options?: {
  applicationId?: string;
  limit?: number;
}): Promise<Array<{ id: string; url: string; description?: string }> | null> {
  const client = getSvixClient();
  if (!client) {
    return null;
  }

  const config = getSvixConfig();
  const appId = options?.applicationId || config.applicationId;

  if (!appId) {
    return null;
  }

  try {
    const result = await client.endpoint.list(appId, {
      limit: options?.limit ?? 50,
    });
    return result.data.map((ep) => ({
      id: ep.id,
      url: ep.url,
      description: ep.description,
    }));
  } catch (error) {
    console.error("[Svix Adapter] Failed to list endpoints:", error);
    throw error;
  }
}

/**
 * Re-export for convenience
 */
export { WebhookVerificationError } from "svix";
